import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { settingsApi } from '@/api/endpoints'
import type { MailSettings } from '@/api/types'
import { Button, Card, Field, Input } from '@/components/ui'
import { errorMessage } from '@/utils/errors'
import { formatDateTime } from '@/utils/format'

/**
 * Odesílání dokladů do Digitoo: Gmail účet (heslo aplikace) a adresa
 * schránky Digitoo. Heslo se zpátky nikdy nenačítá — jen „uloženo“.
 */
export default function SettingsPage() {
  const [current, setCurrent] = useState<MailSettings | null>(null)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [recipient, setRecipient] = useState('')
  const [smtpHost, setSmtpHost] = useState('smtp.gmail.com')
  const [smtpPort, setSmtpPort] = useState('587')
  const [showSmtp, setShowSmtp] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  const apply = (s: MailSettings) => {
    setCurrent(s)
    setUsername(s.username ?? '')
    setRecipient(s.recipient ?? '')
    setSmtpHost(s.smtpHost)
    setSmtpPort(String(s.smtpPort))
    setPassword('')
  }

  useEffect(() => {
    settingsApi
      .mail()
      .then(apply)
      .catch((e) => setError(errorMessage(e, 'Nastavení se nepodařilo načíst.')))
  }, [])

  const save = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    setMessage(null)
    try {
      apply(
        await settingsApi.saveMail({
          username,
          password: password || undefined,
          recipient,
          smtpHost,
          smtpPort: Number(smtpPort) || undefined,
        }),
      )
      setMessage('Uloženo.')
    } catch (err) {
      setError(errorMessage(err, 'Nastavení se nepodařilo uložit.'))
    } finally {
      setBusy(false)
    }
  }

  const test = async () => {
    setBusy(true)
    setError(null)
    setMessage(null)
    try {
      setMessage((await settingsApi.testMail()).message)
    } catch (err) {
      setError(errorMessage(err, 'Zkušební e-mail se nepodařilo odeslat.'))
    } finally {
      setBusy(false)
    }
  }

  if (!current) {
    return error ? <p className="text-sm text-red-600">{error}</p> : <p className="text-slate-400">Načítání…</p>
  }

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold">Nastavení</h1>

      <div className="grid gap-4 lg:grid-cols-[3fr_2fr]">
        <Card>
          <h2 className="mb-1 font-semibold">Odesílání dokladů do Digitoo</h2>
          <p className="mb-4 text-sm text-slate-500">
            Doklady odcházejí z tvého Gmailu do e-mailové schránky Digitoo, každý doklad jako samostatný e-mail
            s přílohou.
          </p>

          <form onSubmit={save} className="space-y-3">
            <Field label="Gmail adresa (odesílatel)">
              <Input
                type="email"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="jmeno@gmail.com"
                autoComplete="off"
              />
            </Field>
            <Field label="Heslo aplikace">
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={current.hasPassword ? '•••• uloženo — vyplň jen pro změnu' : 'abcd efgh ijkl mnop'}
                autoComplete="new-password"
                required={!current.hasPassword}
              />
            </Field>
            <Field label="Adresa Digitoo (příjemce)">
              <Input
                type="email"
                required
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                placeholder="schranka@…"
                autoComplete="off"
              />
            </Field>

            <button
              type="button"
              className="text-xs text-slate-500 hover:text-slate-700"
              onClick={() => setShowSmtp(!showSmtp)}
            >
              {showSmtp ? '▾' : '▸'} SMTP server ({smtpHost}:{smtpPort})
            </button>
            {showSmtp && (
              <div className="grid grid-cols-[3fr_1fr] gap-3">
                <Field label="Server">
                  <Input value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} />
                </Field>
                <Field label="Port">
                  <Input value={smtpPort} onChange={(e) => setSmtpPort(e.target.value)} inputMode="numeric" />
                </Field>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
              <Button type="submit" disabled={busy}>
                Uložit
              </Button>
              <Button type="button" variant="secondary" disabled={busy || !current.configured} onClick={test}>
                Poslat zkušební e-mail sobě
              </Button>
              {current.updatedAt && (
                <span className="text-xs text-slate-400">
                  Naposledy {current.updatedByEmail}, {formatDateTime(current.updatedAt)}
                </span>
              )}
            </div>
            {message && <p className="text-sm text-green-700">{message}</p>}
            {error && <p className="text-sm text-red-600">{error}</p>}
          </form>
        </Card>

        <Card className="text-sm text-slate-600">
          <h2 className="mb-2 font-semibold text-slate-800">Jak získat heslo aplikace</h2>
          <ol className="list-inside list-decimal space-y-2">
            <li>
              V Google účtu musí být zapnuté{' '}
              <a
                href="https://myaccount.google.com/signinoptions/twosv"
                target="_blank"
                rel="noreferrer"
                className="text-brand-600 hover:underline"
              >
                dvoufázové ověření
              </a>
              .
            </li>
            <li>
              Otevři{' '}
              <a
                href="https://myaccount.google.com/apppasswords"
                target="_blank"
                rel="noreferrer"
                className="text-brand-600 hover:underline"
              >
                Hesla aplikací
              </a>
              , pojmenuj ho třeba „mis“ a vytvoř.
            </li>
            <li>16 znaků, které Google ukáže, vlož sem do „Heslo aplikace“ (mezery nevadí).</li>
            <li>Ulož a pošli si zkušební e-mail — přijde na tvůj Gmail, ne do Digitoo.</li>
          </ol>
          <p className="mt-3 text-xs text-slate-400">
            Heslo aplikace jde kdykoli zrušit v Google účtu. V mis je uložené zašifrované a zpátky se nezobrazuje.
          </p>
        </Card>
      </div>
    </div>
  )
}
